import { useEffect, useState } from 'react';
// @mui
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  Divider,
  Chip,
  Skeleton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
// components
import Iconify from '../../../components/Iconify';
import Scrollbar from '../../../components/Scrollbar';
// hooks
import useAuth from "../../../hooks/useAuth"
import useResponsive from "../../../hooks/useResponsive"
// utils
import axios from '../../../utils/axios';

// ----------------------------------------------------------------------

export default function FliersProfilePositionDialog({ isOpen, closeDialog, selectedPosition = [], updatePosition }) {
  const { user } = useAuth();
  const smUp = useResponsive('up', 'sm');
  const [isLoading, setIsLoading] = useState(false);
  const [matchingRoles, setMatchingRoles] = useState([]);
  // developer
  const hasDeveloper = matchingRoles.filter((r) => r.position === "developer").length > 0;
  const [openDeveloper, setOpenDeveloper] = useState(false);
  const [devGroups, setDevGroups] = useState(new Set());
  // designer
  const hasDesigner = matchingRoles.filter((r) => r.position === "designer").length > 0;
  const [openDesigner, setOpenDesigner] = useState(false);
  // marketer
  const hasMarketer = matchingRoles.filter((r) => r.position === "marketer").length > 0;
  const [openMarketer, setOpenMarketer] = useState(false);
  // pm
  const hasProductManager = matchingRoles.filter((r) => r.position === "product-manager").length > 0;
  const [openProductManager, setOpenProductManager] = useState(false);
  // operator
  const hasOperator = matchingRoles.filter((r) => r.position === "operator").length > 0;
  const [openOperator, setOpenOperator] = useState(false);
  // finance
  const hasFinance = matchingRoles.filter((r) => r.position === "finance").length > 0;
  const [openFinance, setOpenFinance] = useState(false);
  // engineer
  const hasEngineer = matchingRoles.filter((r) => r.position === "engineer").length > 0;
  const [openEngineer, setOpenEngineer] = useState(false);

  const [selection, setSelection] = useState(selectedPosition);

  const getMatchingRoles = async () => {
    try {
      setIsLoading(true);
      const companyId = user?.employee?.company?.[0].id;
      const response = await axios.get(`/api/company/${companyId}/fliers/matching-role/`);
      setMatchingRoles(response.data);
      setDevGroups(new Set(response.data.filter((r) => r.position === "developer").map((r) => r.group)));
      setIsLoading(false);
    } catch(err) {
      console.error(err);
      setIsLoading(false);
    }
  };

  const select = (role) => {
    if (selection?.filter((r) => r.role === role.role).length > 0) {
      setSelection(selection.filter((r) => r.role !== role.role));
    } else {
      setSelection([...selection, role]);
    }
  };

  useEffect(() => {
    if (isOpen) {
      getMatchingRoles();
      setSelection(selectedPosition);
      const positions = new Set(selectedPosition?.map((r) => r.position));
      setOpenDeveloper(positions.has("developer"));
      setOpenDesigner(positions.has("designer"));
      setOpenMarketer(positions.has("marketer"));
      setOpenProductManager(positions.has("product-manager"));
      setOpenOperator(positions.has("operator"));
      setOpenFinance(positions.has("finance"));
      setOpenEngineer(positions.has("engineer"));
    } else {
      setIsLoading(false);
    }
  }, [isOpen]);

  const updateAndClose = () => {
    updatePosition(selection);
    closeDialog();
  };
 
  return (
    <Dialog open={isOpen} maxWidth="sm" onClose={closeDialog} fullWidth fullScreen={!smUp}>
      <DialogContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Typography variant="h5" sx={{ mb: { xs: 3, sm: 5 } }}>
          커피챗을 원하는 포지션을 모두 선택해주세요.
        </Typography>
        <Typography variant="h6" sx={{ color: "info.main" }} paragraph>
          포지션 선택하기
        </Typography>
        <Box>
          <Scrollbar
            sx={{
              maxHeight: "500px",
              '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
            }}
          >
            {isLoading && (
              <Skeleton variant="rectangular" width="100%" height={150} />
            )}
            <Stack
              sx={{
                flexShrink: 0,
              }}
              divider={<Divider />}
              spacing={2}
            >
              {hasDeveloper && (
              <Stack spacing={1}>
                <Stack direction="row" alignItems={"center"} justifyContent="space-between"
                  sx={{
                    "&:hover": {
                      cursor: "pointer",
                    }
                  }}
                  onClick={() => setOpenDeveloper(!openDeveloper)}
                >
                  <Typography variant="subtitle1">
                    개발자
                  </Typography>
                  {!openDeveloper && (
                    <Button
                      sx={{
                        color: "text.secondary",
                      }}
                      size="small"
                      variant="text"
                      onClick={() => setOpenDeveloper(!openDeveloper)}
                      endIcon={<Iconify icon="eva:chevron-down-fill" sx={{ width: 20, height: 20 }} />}
                    >
                      모든 포지션 보기
                    </Button>
                  )}
                  {openDeveloper && (
                    <Button
                      sx={{
                        color: "text.secondary",
                      }}
                      size="small"
                      variant="text"
                      onClick={() => setOpenDeveloper(!openDeveloper)}
                      endIcon={<Iconify icon="eva:chevron-up-fill" sx={{ width: 20, height: 20 }} />}
                    >
                      접기
                    </Button>
                  )}
                </Stack>
                <Collapse in={openDeveloper}>
                  <Stack spacing={1} divider={<Divider />}>
                    {[...devGroups]?.map((group, idx) => (
                      <Stack key={idx} direction="row" alignItems={"center"} spacing={3}>
                        <Typography variant="subtitle2" sx={{ color: "text.secondary", minWidth: "65px", textAlign: "right" }}>
                          {group}
                        </Typography>
                        <Stack direction="row" alignItems={"center"} sx={{ flexWrap: "wrap", rowGap: 1, columnGap: 1}}>
                          {matchingRoles.filter((r) => (r.position === "developer" && r.group === group)).map((role, idx) => (
                            <Button
                              key={idx}
                              size="small"
                              variant="outlined"
                              sx={{
                                borderRadius: 2,
                                fontWeight: selection?.filter((r) => r.role == role.role).length > 0 ? "bold" : "normal",
                                border: "1px solid transparent",
                                bgcolor: (theme) => alpha(theme.palette.grey[500], 0.16),
                              }}
                              startIcon={selection?.filter((r) => r.role == role.role).length > 0 ? <Iconify icon="ic:outline-check" /> : null}
                              onClick={() => {select(role)}}
                            >
                              {role.role}
                            </Button>
                          ))}
                        </Stack>
                      </Stack>
                    ))}
                  </Stack>
                </Collapse>
              </Stack>
              )}
              {hasDesigner && (
              <Stack spacing={1}>
                <Stack direction="row" alignItems={"center"} justifyContent="space-between"
                  sx={{
                    "&:hover": {
                      cursor: "pointer",
                    }
                  }}
                  onClick={() => setOpenDesigner(!openDesigner)}
                >
                  <Typography variant="subtitle1">
                    디자이너
                  </Typography>
                  {!openDesigner && (
                    <Button
                      sx={{
                        color: "text.secondary",
                      }}
                      size="small"
                      variant="text"
                      onClick={() => setOpenDesigner(!openDesigner)}
                      endIcon={<Iconify icon="eva:chevron-down-fill" sx={{ width: 20, height: 20 }} />}
                    >
                      모든 포지션 보기
                    </Button>
                  )}
                  {openDesigner && (
                    <Button
                      sx={{
                        color: "text.secondary",
                      }}
                      size="small"
                      variant="text"
                      onClick={() => setOpenDesigner(!openDesigner)}
                      endIcon={<Iconify icon="eva:chevron-up-fill" sx={{ width: 20, height: 20 }} />}
                    >
                      접기
                    </Button>
                  )}
                </Stack>
                <Collapse in={openDesigner}>
                  <Stack direction="row" alignItems={"center"} sx={{ flexWrap: "wrap", rowGap: 1, columnGap: 1}}>
                    {matchingRoles.filter((r) => r.position === "designer").map((role, idx) => (
                      <Button
                        key={idx}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          fontWeight: selection?.filter((r) => r.role == role.role).length > 0 ? "bold" : "normal",
                          border: "1px solid transparent",
                          bgcolor: (theme) => alpha(theme.palette.grey[500], 0.16),
                        }}
                        startIcon={selection?.filter((r) => r.role == role.role).length > 0 ? <Iconify icon="ic:outline-check" /> : null}
                        onClick={() => {select(role)}}
                      >
                        {role.role}
                      </Button>
                    ))}
                  </Stack>
                </Collapse>
              </Stack>
              )}
              {hasMarketer && (
              <Stack spacing={1}>
                <Stack direction="row" alignItems={"center"} justifyContent="space-between"
                  sx={{
                    "&:hover": {
                      cursor: "pointer",
                    }
                  }}
                  onClick={() => setOpenMarketer(!openMarketer)}
                >
                  <Typography variant="subtitle1">
                    마케터
                  </Typography>
                  {!openMarketer && (
                    <Button
                      sx={{
                        color: "text.secondary",
                      }}
                      size="small"
                      variant="text"
                      onClick={() => setOpenMarketer(!openMarketer)}
                      endIcon={<Iconify icon="eva:chevron-down-fill" sx={{ width: 20, height: 20 }} />}
                    >
                      모든 포지션 보기
                    </Button>
                  )}
                  {openMarketer && (
                    <Button
                      sx={{
                        color: "text.secondary",
                      }}
                      size="small"
                      variant="text"
                      onClick={() => setOpenMarketer(!openMarketer)}
                      endIcon={<Iconify icon="eva:chevron-up-fill" sx={{ width: 20, height: 20 }} />}
                    >
                      접기
                    </Button>
                  )}
                </Stack>
                <Collapse in={openMarketer}>
                  <Stack direction="row" alignItems={"center"} sx={{ flexWrap: "wrap", rowGap: 1, columnGap: 1}}>
                    {matchingRoles.filter((r) => r.position === "marketer").map((role, idx) => (
                      <Button
                        key={idx}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          fontWeight: selection?.filter((r) => r.role == role.role).length > 0 ? "bold" : "normal",
                          border: "1px solid transparent",
                          bgcolor: (theme) => alpha(theme.palette.grey[500], 0.16),
                        }}
                        startIcon={selection?.filter((r) => r.role == role.role).length > 0 ? <Iconify icon="ic:outline-check" /> : null}
                        onClick={() => {select(role)}}
                      >
                        {role.role}
                      </Button>
                    ))}
                  </Stack>
                </Collapse>
              </Stack>
              )}
              {hasProductManager && (
              <Stack spacing={1}>
                <Stack direction="row" alignItems={"center"} justifyContent="space-between"
                  sx={{
                    "&:hover": {
                      cursor: "pointer",
                    }
                  }}
                  onClick={() => setOpenProductManager(!openProductManager)}
                >
                  <Typography variant="subtitle1">
                    PM/PO
                  </Typography>
                  {!openProductManager && (
                    <Button
                      sx={{
                        color: "text.secondary",
                      }}
                      size="small"
                      variant="text"
                      onClick={() => setOpenProductManager(!openProductManager)}
                      endIcon={<Iconify icon="eva:chevron-down-fill" sx={{ width: 20, height: 20 }} />}
                    >
                      모든 포지션 보기
                    </Button>
                  )}
                  {openProductManager && (
                    <Button
                      sx={{
                        color: "text.secondary",
                      }}
                      size="small"
                      variant="text"
                      onClick={() => setOpenProductManager(!openProductManager)}
                      endIcon={<Iconify icon="eva:chevron-up-fill" sx={{ width: 20, height: 20 }} />}
                    >
                      접기
                    </Button>
                  )}
                </Stack>
                <Collapse in={openProductManager}>
                  <Stack direction="row" alignItems={"center"} sx={{ flexWrap: "wrap", rowGap: 1, columnGap: 1}}>
                    {matchingRoles.filter((r) => r.position === "product-manager").map((role, idx) => (
                      <Button
                        key={idx}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          fontWeight: selection?.filter((r) => r.role == role.role).length > 0 ? "bold" : "normal",
                          border: "1px solid transparent",
                          bgcolor: (theme) => alpha(theme.palette.grey[500], 0.16),
                        }}
                        startIcon={selection?.filter((r) => r.role == role.role).length > 0 ? <Iconify icon="ic:outline-check" /> : null}
                        onClick={() => {select(role)}}
                      >
                        {role.role}
                      </Button>
                    ))}
                  </Stack>
                </Collapse>
              </Stack>
              )}
              {hasOperator && (
              <Stack spacing={1}>
                <Stack direction="row" alignItems={"center"} justifyContent="space-between"
                  sx={{
                    "&:hover": {
                      cursor: "pointer",
                    }
                  }}
                  onClick={() => setOpenOperator(!openOperator)}
                >
                  <Typography variant="subtitle1">
                    CS • 오퍼레이터
                  </Typography>
                  {!openOperator && (
                    <Button
                      sx={{
                        color: "text.secondary",
                      }}
                      size="small"
                      variant="text"
                      onClick={() => setOpenOperator(!openOperator)}
                      endIcon={<Iconify icon="eva:chevron-down-fill" sx={{ width: 20, height: 20 }} />}
                    >
                      모든 포지션 보기
                    </Button>
                  )}
                  {openOperator && (
                    <Button
                      sx={{
                        color: "text.secondary",
                      }}
                      size="small"
                      variant="text"
                      onClick={() => setOpenOperator(!openOperator)}
                      endIcon={<Iconify icon="eva:chevron-up-fill" sx={{ width: 20, height: 20 }} />}
                    >
                      접기
                    </Button>
                  )}
                </Stack>
                <Collapse in={openOperator}>
                  <Stack direction="row" alignItems={"center"} sx={{ flexWrap: "wrap", rowGap: 1, columnGap: 1}}>
                    {matchingRoles.filter((r) => r.position === "operator").map((role, idx) => (
                      <Button
                        key={idx}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          fontWeight: selection?.filter((r) => r.role == role.role).length > 0 ? "bold" : "normal",
                          border: "1px solid transparent",
                          bgcolor: (theme) => alpha(theme.palette.grey[500], 0.16),
                        }}
                        startIcon={selection?.filter((r) => r.role == role.role).length > 0 ? <Iconify icon="ic:outline-check" /> : null}
                        onClick={() => {select(role)}}
                      >
                        {role.role}
                      </Button>
                    ))}
                  </Stack>
                </Collapse>
              </Stack>
              )}
              {hasFinance && (
              <Stack spacing={1}>
                <Stack direction="row" alignItems={"center"} justifyContent="space-between"
                  sx={{
                    "&:hover": {
                      cursor: "pointer",
                    }
                  }}
                  onClick={() => setOpenFinance(!openFinance)}
                >
                  <Typography variant="subtitle1">
                    재무 • IR/투자 전문가
                  </Typography>
                  {!openFinance && (
                    <Button
                      sx={{
                        color: "text.secondary",
                      }}
                      size="small"
                      variant="text"
                      onClick={() => setOpenFinance(!openFinance)}
                      endIcon={<Iconify icon="eva:chevron-down-fill" sx={{ width: 20, height: 20 }} />}
                    >
                      모든 포지션 보기
                    </Button>
                  )}
                  {openFinance && (
                    <Button
                      sx={{
                        color: "text.secondary",
                      }}
                      size="small"
                      variant="text"
                      onClick={() => setOpenFinance(!openFinance)}
                      endIcon={<Iconify icon="eva:chevron-up-fill" sx={{ width: 20, height: 20 }} />}
                    >
                      접기
                    </Button>
                  )}
                </Stack>
                <Collapse in={openFinance}>
                  <Stack direction="row" alignItems={"center"} sx={{ flexWrap: "wrap", rowGap: 1, columnGap: 1}}>
                    {matchingRoles.filter((r) => r.position === "finance").map((role, idx) => (
                      <Button
                        key={idx}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          fontWeight: selection?.filter((r) => r.role == role.role).length > 0 ? "bold" : "normal",
                          border: "1px solid transparent",
                          bgcolor: (theme) => alpha(theme.palette.grey[500], 0.16),
                        }}
                        startIcon={selection?.filter((r) => r.role == role.role).length > 0 ? <Iconify icon="ic:outline-check" /> : null}
                        onClick={() => {select(role)}}
                      >
                        {role.role}
                      </Button>
                    ))}
                  </Stack>
                </Collapse>
              </Stack>
              )}
              {hasEngineer && (
              <Stack spacing={1}>
                <Stack direction="row" alignItems={"center"} justifyContent="space-between"
                  sx={{
                    "&:hover": {
                      cursor: "pointer",
                    }
                  }}
                  onClick={() => setOpenEngineer(!openEngineer)}
                >
                  <Typography variant="subtitle1">
                    하드웨어 엔지니어
                  </Typography>
                  {!openEngineer && (
                    <Button
                      sx={{
                        color: "text.secondary",
                      }}
                      size="small"
                      variant="text"
                      onClick={() => setOpenEngineer(!openEngineer)}
                      endIcon={<Iconify icon="eva:chevron-down-fill" sx={{ width: 20, height: 20 }} />}
                    >
                      모든 포지션 보기
                    </Button>
                  )}
                  {openEngineer && (
                    <Button
                      sx={{
                        color: "text.secondary",
                      }}
                      size="small"
                      variant="text"
                      onClick={() => setOpenEngineer(!openEngineer)}
                      endIcon={<Iconify icon="eva:chevron-up-fill" sx={{ width: 20, height: 20 }} />}
                    >
                      접기
                    </Button>
                  )}
                </Stack>
                <Collapse in={openEngineer}>
                  <Stack direction="row" alignItems={"center"} sx={{ flexWrap: "wrap", rowGap: 1, columnGap: 1}}>
                    {matchingRoles.filter((r) => r.position === "engineer").map((role, idx) => (
                      <Button
                        key={idx}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          fontWeight: selection?.filter((r) => r.role == role.role).length > 0 ? "bold" : "normal",
                          border: "1px solid transparent",
                          bgcolor: (theme) => alpha(theme.palette.grey[500], 0.16),
                        }}
                        startIcon={selection?.filter((r) => r.role == role.role).length > 0 ? <Iconify icon="ic:outline-check" /> : null}
                        onClick={() => {select(role)}}
                      >
                        {role.role}
                      </Button>
                    ))}
                  </Stack>
                </Collapse>
              </Stack>
              )}
            </Stack>
          </Scrollbar>
        </Box>
      </DialogContent>
      <DialogActions sx={{ m: { xs: 0, sm: 1 } }}>
        <Stack sx={{ width: 1 }}>
          <Stack sx={{ width: 1, pb: 3 }}>
            <Typography variant="h6" sx={{ color: "info.main" }} paragraph>
              선택한 포지션
            </Typography>

            <Stack direction="row" alignItems={"center"} sx={{ flexWrap: "wrap", rowGap: 1, columnGap: 1}}>
              {selection?.map((role, idx) => (
                <Chip
                  key={idx}
                  label={role.role}
                  onClick={() => {select(role)}}
                />
              ))}
            </Stack>
          </Stack>
          <Stack direction="row" justifyContent={"flex-end"}>
            <Button
              size="large"
              variant="text"
              onClick={closeDialog}
              sx={{ px: 6 }}
            >
              닫기
            </Button>
            <Button
              size="large"
              variant="contained"
              onClick={updateAndClose}
              sx={{ px: 6 }}
            >
              선택완료
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  )
}